import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export const CASE_STATISTICS_QUERY_KEYS = {
  all: ['case_statistics'] as const,
  byCase: (caseId: string) => ['case_statistics', 'case', caseId] as const,
  byUserAndCase: (userId: string, caseId: string) =>
    ['case_statistics', 'user', userId, 'case', caseId] as const,
};

export const useCaseStatisticsByCase = (caseId: string | null) => {
  return useQuery({
    queryKey: CASE_STATISTICS_QUERY_KEYS.byCase(caseId || ''),
    queryFn: async () => {
      if (!caseId) throw new Error('Case ID is required');

      const { data, error } = await supabase
        .from('case_statistics')
        .select(
          `
          *,
          profiles!case_statistics_user_id_fkey (
            id,
            username
          )
        `
        )
        .eq('case_id', caseId);

      if (error) throw error;
      return data;
    },
    enabled: !!caseId,
  });
};

export const useCaseStatisticsByUserAndCase = (userId: string | null, caseId: string | null) => {
  return useQuery({
    queryKey: CASE_STATISTICS_QUERY_KEYS.byUserAndCase(userId || '', caseId || ''),
    queryFn: async () => {
      if (!userId || !caseId) throw new Error('User ID and Case ID are required');

      const { data, error } = await supabase
        .from('case_statistics')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!caseId,
  });
};

export const useUpsertCaseStatistics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      updates,
    }: {
      userId: string;
      caseId: string;
      updates: {
        case_selector_visits?: number;
        case_clicks?: number;
        completions?: number;
      };
    }) => {
      const { data, error } = await supabase
        .from('case_statistics')
        .upsert(
          {
            user_id: userId,
            case_id: caseId,
            ...updates,
          },
          { onConflict: 'user_id,case_id' }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CASE_STATISTICS_QUERY_KEYS.byUserAndCase(data.user_id, data.case_id),
      });
      void queryClient.invalidateQueries({
        queryKey: CASE_STATISTICS_QUERY_KEYS.byCase(data.case_id),
      });
      void queryClient.invalidateQueries({ queryKey: CASE_STATISTICS_QUERY_KEYS.all });
    },
  });
};
